/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Appointment list/calendar fetching for ViewAppointments.
 * @module pages/viewAppointments/useViewAppointmentsFetching
 */

import { useCallback } from "react";
import { logger, sanitizeError } from "../../utils/logger.js";
import {
  canLoadAppointments,
  getScopedCalendarAppointments,
  isBulkConfirmDisabled,
} from "../../logic/appointments/appointmentCalendarParams.js";
import { loadCalendarAppointments } from "../../logic/appointments/loadCalendarAppointments.js";
import {
  buildViewAppointmentsListParams,
  fetchViewAppointmentsPage,
} from "./viewAppointmentsPageFetch.js";

/**
 * @param {Object} ctx - ViewAppointments context from `useViewAppointmentsContext`
 * @returns {Object} Fetch helpers and derived appointment accessors
 */
const useViewAppointmentsFetching = (ctx) => {
  const {
    navigate,
    eventData,
    userData,
    selectedBuyerCompany,
    selectedBuyerExecutive,
    currentPage,
    resultsLimit,
    debouncedSearch,
    selectedRequirement,
    selectedStatusFilter,
    selectedRequirementsFilter,
    appointmentsPageRequestRef,
    setIsLoading,
    setFilteredRequests,
    setTotalAppointments,
    setCurrentPage,
    setAppointments,
    setAllAppointmentsForConflict,
    setSenderAppointments,
    appointments,
  } = ctx;

  const canLoadAppointmentsForUser = useCallback(
    (user = userData) =>
      canLoadAppointments({
        eventId: eventData?._id,
        user,
        selectedBuyerCompany,
        selectedBuyerExecutive,
      }),
    [eventData, userData, selectedBuyerCompany, selectedBuyerExecutive],
  );

  const clearAppointmentViews = useCallback(() => {
    setFilteredRequests([]);
    setAppointments([]);
    setAllAppointmentsForConflict([]);
    setSenderAppointments([]);
    setTotalAppointments(0);
  }, [
    setFilteredRequests,
    setAppointments,
    setAllAppointmentsForConflict,
    setSenderAppointments,
    setTotalAppointments,
  ]);

  const getAppointmentListParams = useCallback(
    (page = currentPage) =>
      buildViewAppointmentsListParams({
        page,
        currentPage,
        resultsLimit,
        debouncedSearch,
        selectedRequirement,
        userData,
        selectedBuyerCompany,
        selectedBuyerExecutive,
        selectedStatusFilter,
        selectedRequirementsFilter,
      }),
    [
      currentPage,
      resultsLimit,
      debouncedSearch,
      selectedRequirement,
      userData,
      selectedBuyerCompany,
      selectedBuyerExecutive,
      selectedStatusFilter,
      selectedRequirementsFilter,
    ],
  );

  const fetchAppointmentsPage = useCallback(
    (page = 1) =>
      fetchViewAppointmentsPage({
        page,
        navigate,
        eventData,
        canLoad: canLoadAppointmentsForUser,
        listParams: getAppointmentListParams(page),
        requestRef: appointmentsPageRequestRef,
        setIsLoading,
        setters: {
          setFilteredRequests,
          setTotalAppointments,
          setCurrentPage,
        },
      }),
    [
      navigate,
      eventData,
      canLoadAppointmentsForUser,
      getAppointmentListParams,
      appointmentsPageRequestRef,
      setIsLoading,
      setFilteredRequests,
      setTotalAppointments,
      setCurrentPage,
    ],
  );

  const fetchCalendarAppointments = useCallback(async () => {
    if (!canLoadAppointmentsForUser()) {
      setAppointments([]);
      setAllAppointmentsForConflict([]);
      setSenderAppointments([]);
      return;
    }

    try {
      const calendarData = await loadCalendarAppointments({
        navigate,
        eventId: eventData._id,
        userData,
        selectedBuyerCompany,
        selectedBuyerExecutive,
      });
      setAppointments(calendarData.appointments);
      setAllAppointmentsForConflict(calendarData.conflictItems);
      setSenderAppointments(calendarData.senderAppointments);
    } catch (error) {
      logger.error("Error fetching calendar appointments:", {
        err: sanitizeError(error),
      });
      setAppointments([]);
      setAllAppointmentsForConflict([]);
      setSenderAppointments([]);
    }
  }, [
    canLoadAppointmentsForUser,
    navigate,
    eventData,
    userData,
    selectedBuyerCompany,
    selectedBuyerExecutive,
    setAppointments,
    setAllAppointmentsForConflict,
    setSenderAppointments,
  ]);

  const getCalendarAppointments = useCallback(
    () =>
      getScopedCalendarAppointments(
        userData,
        selectedBuyerCompany,
        selectedBuyerExecutive,
        appointments,
      ),
    [userData, selectedBuyerCompany, selectedBuyerExecutive, appointments],
  );

  const isConfirmButtonDisabled = useCallback(
    () =>
      isBulkConfirmDisabled(
        userData,
        selectedBuyerCompany,
        selectedBuyerExecutive,
      ),
    [userData, selectedBuyerCompany, selectedBuyerExecutive],
  );

  return {
    canLoadAppointmentsForUser,
    clearAppointmentViews,
    fetchAppointmentsPage,
    fetchCalendarAppointments,
    getCalendarAppointments,
    isConfirmButtonDisabled,
  };
};

export default useViewAppointmentsFetching;
